import {
  Box,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Host } from '../../api/types/host';
import AllowedInviteToParking from '../molecules/allowed-invite-to-parking';

import { Scrollbar } from '@/components/atoms/scrollbar';
import AvatarWithInfo from '@/components/molecules/avatar-with-info';
import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { Can } from '@/permissions/can';

interface Props {
  data: Host[];
  error: string;
  isLoading: boolean;
  setSelectedHost: (host: Host) => void;
  actionsSlot: (host: Host) => ReactNode;
  handleOpenUpdateDestinationGroupDialog: () => void;
  handleOpenUpdateBillingGroupDialog: () => void;
  handleOpenUpdateHostDestinationsDialog: () => void;
}

export const HostsTable = ({
  data,
  error,
  isLoading,
  actionsSlot,
  setSelectedHost,
  handleOpenUpdateDestinationGroupDialog,
  handleOpenUpdateBillingGroupDialog,
  handleOpenUpdateHostDestinationsDialog,
}: Props) => {
  const { t } = useTranslation(['hosts', 'common']);

  const columns = [
    {
      id: 'name',
      label: t('hosts:table.name'),
    },
    {
      id: 'destinations',
      label: t('hosts:table.destinations'),
    },
    {
      id: 'allowedParking',
      label: t('hosts:table.allowedParking'),
    },
    {
      id: 'billingGroup',
      label: t('hosts:table.billingGroup'),
    },
    {
      id: 'destinationGroup',
      label: t('hosts:table.destinationGroup'),
    },
    {
      id: 'actions',
      label: '',
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(host => (
              <TableRow hover key={host.id}>
                <TableCell>
                  <Link
                    color="primary"
                    underline="none"
                    component={RouterLink}
                    to={`${host.id}`}
                    variant="inherit">
                    {host.firstName ? (
                      <AvatarWithInfo
                        avatarText={`${host.firstName.slice(0, 1)} ${host.lastName.slice(0, 1)}`}
                        primaryText={`${host.firstName} ${host.lastName}`}
                        secondaryText={host.email}
                        src=""
                      />
                    ) : (
                      <Typography variant={'body2'} color="text.primary">
                        {host.email}
                      </Typography>
                    )}
                  </Link>
                </TableCell>
                <TableCell>
                  <Can I="manageDestinations" a="Hosts" passThrough>
                    {allowed => (
                      <Tooltip
                        title={
                          !allowed
                            ? t('common:notSufficientPermissions')
                            : t('common:clickToManage')
                        }>
                        <span>
                          <Button
                            onClick={() => {
                              setSelectedHost(host);
                              handleOpenUpdateHostDestinationsDialog();
                            }}
                            disabled={!allowed}
                            color="primary"
                            variant="outlined"
                            size="small">
                            {t('hosts:table.destinations')}: {host.destinations.length}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Can>
                </TableCell>
                <TableCell>
                  <AllowedInviteToParking
                    allowInviteToParking={host.allowInviteToParking}
                    hostId={host.id}
                  />
                </TableCell>
                <TableCell>
                  <Can I="manageBillingGroup" a="Hosts" passThrough>
                    {allowed => (
                      <Tooltip
                        title={
                          !allowed
                            ? t('common:notSufficientPermissions')
                            : host.allowInviteToParking
                              ? t('common:clickToManage')
                              : t('hosts:table.switchAllowToInvite')
                        }
                        placement="top">
                        <span>
                          <Button
                            disabled={!host.allowInviteToParking || !allowed}
                            onClick={() => {
                              setSelectedHost(host);
                              handleOpenUpdateBillingGroupDialog();
                            }}
                            color="primary"
                            variant="outlined"
                            size="small">
                            {host.billingGroup
                              ? host.billingGroup.name
                              : t('hosts:table.setBillingGroup')}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Can>
                </TableCell>
                <TableCell>
                  <Can I="manageDestinationGroup" a="Hosts" passThrough>
                    {allowed => (
                      <Tooltip
                        title={
                          !allowed
                            ? t('common:notSufficientPermissions')
                            : t('common:clickToManage')
                        }>
                        <span>
                          <Button
                            onClick={() => {
                              setSelectedHost(host);
                              handleOpenUpdateDestinationGroupDialog();
                            }}
                            color="primary"
                            variant="outlined"
                            size="small"
                            disabled={!allowed}>
                            {host.destinationGroup
                              ? host.destinationGroup.name
                              : t('hosts:table.setDestinationGroup')}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Can>
                </TableCell>
                <TableCell align="right">{actionsSlot(host)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
      <DataStatusIndicator dataLength={data?.length} error={error} isFetching={isLoading} />
    </Box>
  );
};
